'use client'

import React, { useState } from 'react'
import { Button } from './Button'
import { Progress } from './Progress'
import { Alert } from './Alert'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './Card'
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Trash2,
  FileX,
  Users,
} from 'lucide-react'

interface FileUploadProps {
  title?: string
  description?: string
  allowedTypes?: string[]
  maxSize?: number // MB
  onUpload: (file: File) => Promise<{
    totalRows: number
    successRows: number
    errorRows: number
    errors: string[]
  }>
  onSuccess?: () => void
  templateUrl?: string
  templateFileName?: string
  showTemplateDownload?: boolean
}

export function FileUpload({
  title = '파일 업로드',
  description = '파일을 드래그하여 업로드하거나 클릭하여 선택하세요',
  allowedTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
  ],
  maxSize = 10, // 10MB
  onUpload,
  onSuccess,
  templateUrl,
  templateFileName = '템플릿.xlsx',
  showTemplateDownload = false,
}: FileUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle')
  const [dragActive, setDragActive] = useState(false)
  const [uploadResult, setUploadResult] = useState<{
    totalRows: number
    successRows: number
    errorRows: number
    errors: string[]
  } | null>(null)

  // 드래그 이벤트 핸들러
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  // 파일 드롭 핸들러
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      handleFileSelect(file)
    }
  }

  // 파일 선택 핸들러
  const handleFileSelect = (file: File) => {
    if (!allowedTypes.includes(file.type)) {
      setUploadStatus('error')
      setUploadResult({
        totalRows: 0,
        successRows: 0,
        errorRows: 0,
        errors: ['지원되지 않는 파일 형식입니다.'],
      })
      return
    }

    if (file.size > maxSize * 1024 * 1024) {
      setUploadStatus('error')
      setUploadResult({
        totalRows: 0,
        successRows: 0,
        errorRows: 0,
        errors: [
          `파일 크기가 너무 큽니다. ${maxSize}MB 이하의 파일만 업로드 가능합니다.`,
        ],
      })
      return
    }

    setSelectedFile(file)
    setUploadStatus('idle')
    setUploadResult(null)
  }

  // 파일 업로드
  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    setUploadProgress(0)
    setUploadStatus('idle')

    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval)
          return 90
        }
        return prev + 10
      })
    }, 200)

    try {
      const result = await onUpload(selectedFile)

      clearInterval(progressInterval)
      setUploadProgress(100)

      setUploadStatus('success')
      setUploadResult(result)

      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('파일 업로드 중 오류 발생:', error)
      clearInterval(progressInterval)
      setUploadStatus('error')
      setUploadResult({
        totalRows: 0,
        successRows: 0,
        errorRows: 0,
        errors: ['업로드 중 오류가 발생했습니다. 다시 시도해주세요.'],
      })
    } finally {
      setIsUploading(false)
    }
  }

  // 파일 제거
  const removeFile = () => {
    setSelectedFile(null)
    setUploadStatus('idle')
    setUploadResult(null)
  }

  // 템플릿 다운로드
  const downloadTemplate = () => {
    if (!templateUrl) return

    const link = document.createElement('a')
    link.href = templateUrl
    link.download = templateFileName
    link.click()
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 템플릿 다운로드 */}
          {showTemplateDownload && templateUrl && (
            <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
              <div>
                <h4 className="font-medium text-blue-900">업로드 템플릿</h4>
                <p className="text-sm text-blue-700">
                  올바른 형식으로 데이터를 입력하기 위해 템플릿을
                  다운로드하세요.
                </p>
              </div>
              <Button
                onClick={downloadTemplate}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                템플릿 다운로드
              </Button>
            </div>
          )}

          {/* 파일 업로드 영역 */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-500 bg-blue-50'
                : selectedFile
                ? 'border-green-500 bg-green-50'
                : 'border-gray-300 bg-gray-50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {selectedFile ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-8 h-8 text-green-600" />
                  <span className="font-medium text-green-900">
                    {selectedFile.name}
                  </span>
                </div>
                <div className="flex justify-center gap-2">
                  <Button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="flex items-center gap-2"
                  >
                    {isUploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        업로드 중...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        업로드
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={removeFile}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    제거
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    파일을 드래그하여 업로드하거나 클릭하여 선택하세요
                  </p>
                  <p className="text-sm text-gray-500">
                    Excel (.xlsx, .xls) 또는 CSV 파일만 지원됩니다
                  </p>
                </div>
                <Button
                  onClick={() => document.getElementById('file-input')?.click()}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  파일 선택
                </Button>
              </div>
            )}
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) =>
                e.target.files?.[0] && handleFileSelect(e.target.files[0])
              }
              className="hidden"
            />
          </div>

          {/* 업로드 진행률 */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>업로드 중...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="w-full" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* 업로드 결과 */}
      {uploadStatus !== 'idle' && uploadResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {uploadStatus === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              업로드 결과
            </CardTitle>
          </CardHeader>
          <CardContent>
            {uploadStatus === 'success' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-900">
                      {uploadResult.totalRows}
                    </p>
                    <p className="text-sm text-blue-700">총 처리 건수</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-900">
                      {uploadResult.successRows}
                    </p>
                    <p className="text-sm text-green-700">성공</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <FileX className="w-8 h-8 text-red-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-900">
                      {uploadResult.errorRows}
                    </p>
                    <p className="text-sm text-red-700">실패</p>
                  </div>
                </div>

                {uploadResult.errorRows > 0 && (
                  <Alert.Alert>
                    <AlertCircle className="w-4 h-4" />
                    <Alert.Description>
                      <div className="space-y-2">
                        <p className="font-medium">
                          처리 실패 항목이 있습니다:
                        </p>
                        <ul className="text-sm space-y-1">
                          {uploadResult.errors.map((error, index) => (
                            <li key={index} className="text-red-700">
                              • {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Alert.Description>
                  </Alert.Alert>
                )}
              </div>
            ) : (
              <Alert.Alert>
                <AlertCircle className="w-4 h-4" />
                <Alert.Description>
                  <div className="space-y-2">
                    <p className="font-medium">업로드 실패</p>
                    <ul className="text-sm space-y-1">
                      {uploadResult.errors.map((error, index) => (
                        <li key={index} className="text-red-700">
                          • {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Alert.Description>
              </Alert.Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
